import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Divider,
    Grid,
    makeStyles,
    Theme,
    Fade,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import type { IServiceClient } from 'src/interfaces/serviceClient';
import type { TServiceClientVehicle } from 'src/interfaces/serviceClient';
import { VehicleTabs } from './VehicleTabs';
import { ServiceClientInfo } from './ServiceClientInfo';
import { NewServiceForm } from './NewServiceForm';
import { ServiceHistoryCards } from './ServiceHistoryCards';
import useAuth from 'src/hooks/useAuth';
import { useServiceClient } from 'src/hooks/useServiceClient';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    SERVICE_CLIENT_AUTH,
    ASSIGN_AGENT_AUTH,
    CREATE_PERMISSION,
    UPDATE_PERMISSION,
    DELETE_PERMISSION,
} from 'src/constants/permissions';
import { Plus } from 'react-feather';
import { SendWhatsAppServiceClient } from 'src/components/ServiceClients/WhatsApp/SendWhatsAppServiceClient';
import {
    canSendWhatsAppToServiceClient,
    canMakeCallToServiceClient,
} from 'src/modules/serviceClients/helpers';
import { MakeCallServiceClient } from 'src/components/ServiceClients/Calls';
import { AssignLead } from 'src/components/Lead/Assignation';
import { Button } from 'src/shared/ui';
import { SendDocumentation } from 'src/components/Lead/Documentation';
import { useSnackbar } from 'notistack';
import useLead from 'src/hooks/useLead';

const useStyles = makeStyles((theme: Theme) => ({
    tabsCardContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(10),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    whatsAppServiceClient: {
        marginTop: theme.spacing(2),
    },
    tabsWrapper: {
        flexGrow: 1,
        minWidth: 0,
        overflow: 'hidden',
    },
    newServiceToggleCardContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
}));

type ServiceClientDetailsProps = {
    client: IServiceClient | null;
    vehicles: TServiceClientVehicle[];
};

export const ServiceClientDetails = ({
    client,
    vehicles,
}: ServiceClientDetailsProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();
    const history = useHistory();
    const { deleteServiceClientVehicleServiceHistory } = useServiceClient();
    const { success } = useLead();
    const [selectedTab, setSelectedTab] = useState(0);
    const [isNewServiceFormEnabled, setIsNewServiceFormEnabled] =
        useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const canUpdateServiceClient = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: SERVICE_CLIENT_AUTH,
        crud: UPDATE_PERMISSION,
    });
    const canCreateVehicle = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: SERVICE_CLIENT_AUTH,
        crud: CREATE_PERMISSION,
    });
    const canDeleteServiceHistory = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: SERVICE_CLIENT_AUTH,
        crud: DELETE_PERMISSION,
    });
    const canAssignAgentToLead = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: ASSIGN_AGENT_AUTH,
        crud: UPDATE_PERMISSION,
    });
    const assignLeadStoreId =
        client?.store &&
        (typeof client.store === 'string'
            ? client.store
            : client.store._id);
    const addVehiclePath = client?._id
        ? `/management/service-clients/${client._id}/create/vehicle`
        : null;
    const selectedVehicleId = vehicles?.[selectedTab]?.serviceVehicleId;
    const vehicleEntries = Array.isArray(client?.service?.vehicles)
        ? client.service.vehicles
        : client?.service?.vehicles
            ? [client.service.vehicles]
            : [];
    const selectedVehicleEntry =
        vehicleEntries.find(
            (entry: TServiceClientVehicle) => entry?._id === selectedVehicleId
        ) || vehicleEntries[selectedTab];
    const selectedVehicleHistory = selectedVehicleEntry?.serviceHistory || [];
    const handleDeleteServiceHistory = async (serviceHistoryId: string) => {
        if (!client?._id || !selectedVehicleId) return;
        await deleteServiceClientVehicleServiceHistory(
            client._id,
            selectedVehicleId,
            serviceHistoryId
        );
    };
    const selectedVehicle = vehicles?.[selectedTab];

    useEffect(() => {
        if (success) {
            enqueueSnackbar(t(success), {
                variant: 'success',
            });
        }
    }, [success]);

    return (
        <Fade in={true} timeout={600}>
            <Box mt={3} data-testid="service-client-details">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card elevation={0}>
                            <CardContent className={classes.tabsCardContent}>
                                <Box className={classes.tabsWrapper}>
                                    {vehicles?.length > 0 && (
                                        <VehicleTabs
                                            vehicles={vehicles}
                                            value={selectedTab}
                                            onChange={(_, v) =>
                                                setSelectedTab(v)
                                            }
                                        />
                                    )}
                                </Box>
                                {canCreateVehicle && addVehiclePath && (
                                    <Button
                                        onClick={() => {
                                            history.push(addVehiclePath);
                                        }}
                                        variant="contained"
                                        data-testid="service-client-details-add-vehicle"
                                        startIcon={<Plus />}
                                    >
                                        {String(
                                            t('serviceClient.CreateVehicle')
                                        )}
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <ServiceClientInfo
                            client={client}
                            vehicles={vehicles}
                            selectedTab={selectedTab}
                            canUpdateServiceClient={canUpdateServiceClient}
                        />
                    </Grid>
                    <Grid item lg={6} xs={12}>
                        <Fade
                            in={isNewServiceFormEnabled}
                            timeout={{ enter: 300, exit: 250 }}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Box>
                                <NewServiceForm
                                    serviceClientId={client?._id}
                                    vehicleId={selectedVehicleId}
                                    isEnabled={isNewServiceFormEnabled}
                                    onSuccess={() =>
                                        setIsNewServiceFormEnabled(false)
                                    }
                                />
                            </Box>
                        </Fade>
                        <Fade
                            in={!isNewServiceFormEnabled}
                            timeout={{ enter: 300, exit: 250 }}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Box>
                                <ServiceHistoryCards
                                    history={selectedVehicleHistory}
                                    canDelete={canDeleteServiceHistory}
                                    onDelete={handleDeleteServiceHistory}
                                />
                            </Box>
                        </Fade>
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <Card elevation={0}>
                            <CardHeader
                                title={String(
                                    t('serviceClient.NewServiceTitle')
                                )}
                            />
                            <Divider />
                            <CardContent
                                className={classes.newServiceToggleCardContent}
                            >
                                <Button
                                    variant="contained"
                                    intent={
                                        isNewServiceFormEnabled
                                            ? 'error'
                                            : 'primary'
                                    }
                                    onClick={() =>
                                        setIsNewServiceFormEnabled(
                                            (prev) => !prev
                                        )
                                    }
                                    disabled={!selectedVehicleId}
                                >
                                    {String(
                                        t(
                                            isNewServiceFormEnabled
                                                ? 'Buttons.Cancel'
                                                : 'serviceClient.AddService'
                                        )
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                        <SendDocumentation lead={client} vehicle={selectedVehicle} className={classes.whatsAppServiceClient} />
                        {canSendWhatsAppToServiceClient(client) && client && (
                            <SendWhatsAppServiceClient
                                serviceClient={client}
                                vehicle={selectedVehicle}
                                className={classes.whatsAppServiceClient}
                                elevation={0}
                            />
                        )}
                        {canMakeCallToServiceClient(client) && client && (
                            <MakeCallServiceClient
                                client={client}
                                className={classes.whatsAppServiceClient}
                                elevation={0}
                            />
                        )}
                        {canAssignAgentToLead && client && (
                            <AssignLead
                                area={client.type}
                                carType={client.carType}
                                storeId={assignLeadStoreId}
                                leadId={client._id}
                                currentAgent={client.agent?._id}
                                className={classes.whatsAppServiceClient}
                                elevation={0}
                                isPermissionGranted={canAssignAgentToLead}
                            />
                        )}
                    </Grid>
                </Grid>
            </Box>
        </Fade>
    );
};
